import {useEffect, useState, Fragment, useCallback} from 'react'
import {useQuery, useMutation} from '@apollo/client'
import {connect} from 'react-redux'
import {Map, fromJS} from 'immutable'
import PropTypes from 'prop-types'
import {readEndpoint} from 'redux-json-api'

import MediumModal from '../../../../shared_components/mui_base_components/modals/medium_modal/MediumModal'
import TfaInfoDisplay from './tfa_info/TfaInfoDisplay'
import ManageTfaSetup from './ManageTfaSetup'

import {currentUserConfig} from '../../../../lib/userTools'
import {cleanGraphQLResponse} from '../../../../lib/graphQLTools'
import {
  findRawResourceById,
  findRawResourceByAttributeId,
  findResourceByAttributeId
} from '../../../../lib/plan_data/userData'
import {BACKUP_CODES, TFA_ENROLLMENTS} from '../../../../graphql/queries/tfa'
import {CREATE_TFA_ENROLLMENT, DELETE_TFA_ENROLLMENT, TOGGLE_TFA_STATUS, CREATE_TFA_BACKUP_CODES} from '../../../../graphql/mutations/tfa'

import TfaConfigStore from '../../stores/TfaConfigStore'
import {isMandatoryTfaEnabled} from '../../../../lib/tfaTools'

import './tfa-info.scss'
import TfaApi from '../../web/TfaApi'


export const TfaInfo = props => {
  const [tfaConfig, setTfaConfig] = useState(fromJS(TfaConfigStore.getTfaConfig() || {}))
  const [showModal, setShowModal] = useState(false)
  const [mode, setMode] = useState('')
  const [backupCodesStatus, setBackupCodesStatus] = useState({status: '', mode})
  const {data, loading: loadingBackupCodes} = useQuery(BACKUP_CODES)
  const backupCodes = cleanGraphQLResponse(data, 'backupCodes')
  const {data: tfaEnrollmentsData, loading: loadingTfaEnrollments} = useQuery(TFA_ENROLLMENTS)
  const tfaEnrollments = cleanGraphQLResponse(tfaEnrollmentsData, 'tfaEnrollments')
  const phoneEnrollment = findResourceByAttributeId({resourceList: tfaEnrollments, attribute: 'mode', id: 'phone'})
  const appEnrollment = findResourceByAttributeId({resourceList: tfaEnrollments, attribute: 'mode', id: 'app'})
  const [createTfaEnrollment, {data: createTfaEnrollmentData, reset}] = useMutation(CREATE_TFA_ENROLLMENT, {awaitRefetchQueries: true, refetchQueries: [{query: TFA_ENROLLMENTS}]})
  const createdAppEnrollment = cleanGraphQLResponse(createTfaEnrollmentData, 'createTfaEnrollment', Map())
  const [deleteTfaEnrollment] = useMutation(DELETE_TFA_ENROLLMENT, {awaitRefetchQueries: true, refetchQueries: [{query: TFA_ENROLLMENTS}]})
  const [toggleTfaStatus] = useMutation(TOGGLE_TFA_STATUS, {awaitRefetchQueries: true, refetchQueries: [{query: TFA_ENROLLMENTS}, {query: BACKUP_CODES}]})
  const [createTfaBackupCodes, {data: createTfaBackupCodesData, reset: resetCreatedBackupCodes}] = useMutation(CREATE_TFA_BACKUP_CODES, {awaitRefetchQueries: true, refetchQueries: [{query: BACKUP_CODES}]})
  const tfaBackup = cleanGraphQLResponse(createTfaBackupCodesData, 'createTfaBackupCodes', Map())
  const {firmConfig, organizationConfig, userConfig, firm} = props
  const loadOrgConfig =
    (firm.isEmpty() || firm.get('organization-id')) &&
    organizationConfig.isEmpty()
  const loadClientDetails = userConfig.get('client') && loadOrgConfig
  const loadFirmUserDetails = userConfig.get('firm-id') && loadOrgConfig
  const appEnrollmentOnly = props.userConfig.get('admin') || props.userConfig.get('quarterback') || props.userConfig.get('account-manager')

  const onUpdate = () => {
    setTfaConfig(fromJS(TfaConfigStore.getTfaConfig()))
  }

  useEffect(() => {
    if (loadClientDetails) {
      props.readEndpoint(
        `everplans/${userConfig.get(
          'everplan-id'
        )}?include=firm.firm-config,firm.organization.organization-config`
      )
    }

    if (loadFirmUserDetails) {
      props.readEndpoint(
        `firms/${userConfig.get(
          'firm-id'
        )}?include=firm-config,organization.organization-config`
      )
    }

    TfaConfigStore.addChangeListener(onUpdate)

    return () => TfaConfigStore.removeChangeListener(onUpdate)
  }, [])

  useEffect(() => {
    props.readEndpoint('user-configs')
  }, [tfaConfig.get('status')])

  useEffect(() => {
    if (!loadingTfaEnrollments && !tfaEnrollments.isEmpty()) {
      const danglingEnrollments = tfaEnrollments.filter(enrollment => !enrollment.get('enabled-at'))

      danglingEnrollments.forEach(enrollment => {
        deleteTfaEnrollment({variables: {id: enrollment.get('id')}})
      })
    }
  }, [loadingTfaEnrollments])

  const tfaRequired = () => (
    tfaEnrollments.size === 1 && (
      userConfig.get('quarterback') ||
      userConfig.get('admin') ||
      userConfig.get('account-manager') ||
      isMandatoryTfaEnabled({organizationConfig, firmConfig}) ||
      (firm.get('firm-type') === 'funeral_home')
    )
  )

  const removeTfaEnrollment = ({id, onCompleted}) => {
    deleteTfaEnrollment({
      variables: {id},
      onCompleted
    })
  }

  const toggleModal = useCallback(() => {
    if (showModal && mode === 'recovery') {
      setBackupCodesStatus(prevState => ({...prevState, status: 'initial', mode}))
      resetCreatedBackupCodes?.()
    } else if (showModal && mode === 'app' && !appEnrollment.get('enabled-at')) {
      removeTfaEnrollment({id: appEnrollment.get('id')})
    } else if (showModal && mode === 'phone' && !phoneEnrollment.isEmpty() && !phoneEnrollment.get('enabled-at')) {
      removeTfaEnrollment({id: phoneEnrollment.get('id')})
    }

    setShowModal(!showModal)
  }, [showModal, mode, appEnrollment, phoneEnrollment])

  const manageTfaSetup = currentMode => {
    setMode(currentMode)
    setShowModal(true)
  }

  const createAppEnrollment = onCompleted => {
    if (onCompleted) {
      const input = {name: 'Authenticator app', mode: 'app', category: 'primary'}

      createTfaEnrollment({variables: {input}, onCompleted})
    }
  }

  const onDelete = ({id, onCompleted}) => {
    if (tfaEnrollments.size <= 1) {
      const tfaConfigId = tfaConfig.get('id')

      toggleTfaStatus({
        variables: {input: {tfaConfigId, enabled: false}},
        onCompleted: () => {
          TfaApi.fetchTfaConfig(tfaConfigId)
          if (onCompleted)
            onCompleted()
        }
      })
    } else {
      removeTfaEnrollment({id, onCompleted})
    }
  }

  const generateBackupCodes = onCompleted => {
    createTfaBackupCodes({variables: {tfaConfigId: tfaConfig.get('id')}, onCompleted: () => {
      setBackupCodesStatus(prevState => ({...prevState, status: 'generated', mode}))
      if (onCompleted)
        onCompleted()
    }})
  }

  return (
    <Fragment>
      <TfaInfoDisplay
        loading={loadClientDetails || loadFirmUserDetails || loadingBackupCodes || loadingTfaEnrollments}
        appEnrollment={appEnrollment}
        phoneEnrollment={phoneEnrollment}
        createAppEnrollment={createAppEnrollment}
        tfaConfig={tfaConfig}
        organizationConfig={organizationConfig}
        isFuneralHome={firm.get('firm-type') === 'funeral_home'}
        isFirmUser={userConfig.get('firm-id') && !userConfig.get('client')}
        isQuarterback={userConfig.get('quarterback')}
        backupCodes={backupCodes}
        manageTfaSetup={manageTfaSetup}
      />
      <MediumModal className='manage-tfa-setup' open={showModal} onClose={toggleModal}>
        <ManageTfaSetup
          appEnrollment={appEnrollment}
          appEnrollmentOnly={appEnrollmentOnly}
          createdAppEnrollment={createdAppEnrollment}
          phoneEnrollment={phoneEnrollment}
          backupCodes={backupCodes}
          backupCodesStatus={backupCodesStatus}
          generateBackupCodes={generateBackupCodes}
          mode={mode}
          onDelete={onDelete}
          tfaConfig={tfaConfig}
          tfaBackup={tfaBackup}
          toggleModal={toggleModal}
          toggleTfaStatus={toggleTfaStatus}
          tfaEnrollments={tfaEnrollments}
          resetCreateAppEnrollment={reset}
          removeTfaEnrollment={removeTfaEnrollment}
          manageTfaSetup={manageTfaSetup}
          userConfig={userConfig}
          tfaRequired={tfaRequired()}
        />
      </MediumModal>
    </Fragment>
  )
}

TfaInfo.propTypes = {
  firm: PropTypes.instanceOf(Map),
  firmConfig: PropTypes.instanceOf(Map),
  readEndpoint: PropTypes.func,
  organizationConfig: PropTypes.instanceOf(Map),
  userConfig: PropTypes.instanceOf(Map)
}

const mapStateToProps = state => {
  const userConfig = currentUserConfig(state.api)

  const firmOwnership = findRawResourceByAttributeId({
    rawResource: state.api['firm-ownerships'],
    attribute: 'everplan-id',
    id: userConfig.get('everplan-id')
  })

  const firm = findRawResourceById({
    rawResource: state.api.firms,
    id: userConfig.get('firm-id') || firmOwnership.get('ownable-id')
  })

  const organizationConfig = findRawResourceByAttributeId({
    rawResource: state.api['organization-configs'],
    attribute: 'organization-id',
    id: firm.get('organization-id')
  })

  const firmConfig = findRawResourceByAttributeId({
    rawResource: state.api['firm-configs'],
    attribute: 'firm-id',
    id: firm.get('id')
  })

  return {
    firm,
    firmConfig,
    organizationConfig,
    userConfig
  }
}


export default connect(mapStateToProps, {readEndpoint})(TfaInfo)
